import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Cart drawer shipping row (snippets/cart-shipping-meter.liquid).
 * Draws the free shipping spend meter from the shipping table text, e.g. "GBP£95", and redraws whenever the
 * drawer re-renders with a new cart total (the theme's morph updates `data-total`).
 */

/** Where the threshold tick sits, as a percentage of the bar (the meter isn't to scale) */
const SPLIT = 60;

/**
 * Splits shipping table text such as "AUD$195", "US$150" or "1,325؋AFN" into a currency code and a whole amount.
 * @param {string | undefined} text
 * @returns {{ code: string, amount: number } | null}
 */
function parseTableAmount(text) {
  if (!text) return null;
  const digits = text.replace(/[^0-9]/g, '');
  if (!digits) return null;

  let code = text.match(/[A-Z]{3}/)?.[0] ?? '';
  if (text.startsWith('US$')) code = 'USD';
  else if (text.startsWith('HK$')) code = 'HKD';

  return { code, amount: Number(digits) };
}

/** @param {string} value */
function escapeHtml(value) {
  return value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

class CartShippingMeter extends HTMLElement {
  static observedAttributes = ['data-total'];

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.render();
  }

  render() {
    const { dataset } = this;
    const currency = dataset.currency ?? '';
    const format = dataset.moneyFormat ?? '{{amount}}';
    const unit = convertMoneyToMinorUnits('1', currency) ?? 100;
    const total = convertMoneyToMinorUnits(dataset.total ?? '0', currency) ?? 0;
    const free = parseTableAmount(dataset.free);
    const cost = parseTableAmount(dataset.cost);
    const thresholdHkd = Number(dataset.thresholdHkd) || 0;
    const rate = currency === 'HKD' ? 1 : Number(window.Shopify?.currency?.rate) || 0;
    /** @param {number} amount */
    const money = (amount) => formatMoney(amount, format, currency);

    // Threshold: the table figure when it's in the cart currency, otherwise the HK$ rule threshold converted
    // (which is the table figure converted through its own ratio). Rounded up so we never unlock early.
    let threshold = 0;
    let thresholdLabel = '';
    if (free && free.code === currency) {
      threshold = free.amount * unit;
      thresholdLabel = dataset.free ?? '';
    } else if (free && thresholdHkd && rate) {
      threshold = Math.ceil(thresholdHkd * rate) * unit;
      thresholdLabel = money(threshold);
    }

    // Cost below the threshold: the table's price ("From AUD$17"), converted the same way when it's in another
    // currency
    let costPrice = '';
    if (cost && cost.code === currency) {
      costPrice = dataset.cost ?? '';
    } else if (cost && free && thresholdHkd && rate) {
      costPrice = money(Math.ceil((cost.amount * thresholdHkd * rate) / free.amount) * unit);
    }
    const costLabel = costPrice ? (dataset.textFrom ?? '[price]').replace('[price]', costPrice) : dataset.textCalculated;

    const mode = !threshold ? 'none' : total >= threshold ? 'unlocked' : 'under';
    const percent = mode === 'unlocked' ? 100 : mode === 'under' ? Math.min(SPLIT, (SPLIT * total) / threshold) : 0;

    const stateElement = this.#ref('state');
    stateElement?.classList.remove('cart-shipping--pending', 'cart-shipping--none', 'cart-shipping--under', 'cart-shipping--unlocked');
    stateElement?.classList.add(`cart-shipping--${mode}`);
    this.#setText('value', mode === 'unlocked' ? dataset.textFree : costLabel);
    if (mode === 'none') return;

    this.#ref('fill')?.style.setProperty('width', `${percent}%`);
    this.#ref('dot')?.style.setProperty('left', `${percent}%`);
    this.#setText('threshold', thresholdLabel);

    const message = this.#ref('message');
    if (!message) return;
    if (mode === 'under') {
      const html = (dataset.textAdd ?? '').replace('[amount]', escapeHtml(money(threshold - total)));
      if (message.innerHTML !== html) message.innerHTML = html;
    } else if (message.textContent !== dataset.textUnlocked) {
      message.textContent = dataset.textUnlocked ?? '';
    }
  }

  /** @param {string} name */
  #ref(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-ref="${name}"]`));
  }

  /**
   * @param {string} name
   * @param {string | undefined} text
   */
  #setText(name, text) {
    const element = this.#ref(name);
    if (element && element.textContent !== text) element.textContent = text ?? '';
  }
}

if (!customElements.get('cart-shipping-meter')) {
  customElements.define('cart-shipping-meter', CartShippingMeter);
}
